import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import tw, { styled } from 'twin.macro';

import {
  FEATURE_GATES,
  GlobalFeatureGateFilter
} from '@ubisend/pulse-auth';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useCurrentTimeZone
} from '@ubisend/pulse-hooks';
import {
  Toggle,
  Flex,
  Heading3,
  Span,
  Tag,
  TextInput,
  InnerPanel,
  Label,
  Button,
  useNotification,
  Placeholder,
  Select,
  formatSelectOption,
  Divider,
  TextArea
} from '@ubisend/pulse-components';
import { ActiveHours } from '@ubisend/pulse-tickets';

import { updateSettings as updateSettingsApi } from '../api';

const defaultPrompts = [
  "You are a polite, empathetic customer support assistant. Use plain language, confirm user intent, offer one immediate troubleshooting step, then propose 2 next steps with estimated time to resolution.",
  "You are a concise, helpful, and safety-minded AI assistant. Prioritize clarity, ask clarifying questions before making assumptions, and provide step-by-step guidance when appropriate.",
  "You are a friendly and knowledgeable support bot. Always greet the user, summarize their issue, and provide a clear solution with links to relevant documentation.",
  "You are a proactive assistant. Anticipate follow-up questions, provide short answers with optional deep dives, and always confirm if the issue is resolved.",
  "You are a calm and reassuring AI helper. Validate the user's concern, explain the situation clearly, and offer practical steps with estimated resolution times.",
  "You are a fast and efficient AI assistant. Focus on resolving issues quickly while maintaining professionalism and clarity.",
  "You are a multilingual support bot. Detect language automatically and respond in the user's preferred language with localized resources.",
  "You are a technical support assistant. Provide accurate, jargon-free explanations for software and hardware issues.",
  "You are a troubleshooting expert. Ask targeted questions to narrow down the issue and guide the user through resolution.",
  "You are a documentation-driven assistant. Reference internal knowledge base articles and summarize them in user-friendly language.",
  "You are a customer-first assistant. Prioritize empathy, listen actively, and ensure the user feels heard before offering solutions.",
  "You are a conversational AI helper. Maintain a natural tone, avoid robotic phrasing, and adapt responses based on user sentiment."
];

const SmallButtonWrapper = styled.div`
  & > button {
    ${tw`px-2 py-1 text-xs h-6`}
  }
`;

const TicketAssignmentAlgorithmSelect = ({ value, ...props }) => {
  const { data, isLoading, isSuccess, isError } = useQuery({
    queryKey: ['tickets/ubidesk/assign-algorithms']
  });

  if (isError) {
    return <Span colour="danger">Unable to load assignment algorithms.</Span>;
  }

  const options = isSuccess ? data.data.map(formatSelectOption) : [];
  const selectedValue =
    isSuccess && value
      ? formatSelectOption(data.data.find(algorithm => algorithm.id === value))
      : null;

  return (
    <Select
      options={options}
      isLoading={isLoading}
      value={selectedValue}
      noOptionsMessage={() => 'No assignment algorithms available'}
      placeholder={isLoading ? 'Loading algorithms...' : 'Select an algorithm'}
      {...props}
    />
  );
};

TicketAssignmentAlgorithmSelect.propTypes = {
  value: PropTypes.number.isRequired
};

const LiveChatSettings = () => {
  const [fullSettings, setFullSettings] = useState(null);
  const [generalSettings, setGeneralSettings] = useState(null);
  const [sentimentSettings, setSentimentSettings] = useState(null);
  const [generativeSettings, setGenerativeSettings] = useState(null);
  const [agentSettings, setAgentSettings] = useState(null);
  const [promptDraft, setPromptDraft] = useState('');

  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();
  const timeZone = useCurrentTimeZone();

  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ['live-chat/settings']
  });

  const { mutate: updateSettings, isPending: isUpdating } = useMutation({
    mutationFn: updateSettingsApi,
    onSuccess: () => {
      showSuccess('Successfully updated live chat settings.');
      queryClient.invalidateQueries({ queryKey: ['live-chat/settings'] });
      queryClient.invalidateQueries({ queryKey: ['live-chat/suggestions'] });
    }
  });

  useEffect(() => {
    if (isSuccess) {
      const { algorithm, ...rest } = data.data;
      const full = { ...rest, ticket_assign_algorithm_id: algorithm.id };
      setFullSettings(full);

      setGeneralSettings({
        enabled: full.enabled,
        has_follow_ups_enabled: full.has_follow_ups_enabled,
        has_agent_feedback_enabled: full.has_agent_feedback_enabled
      });

      setSentimentSettings({
        has_consecutive_negative_message_enabled: full.has_consecutive_negative_message_enabled,
        consecutive_negative_message: full.consecutive_negative_message
      });

      setGenerativeSettings({
        has_auto_generate_responses_enabled: full.has_auto_generate_responses_enabled,
        generative_prompt: full.generative_prompt
      });

      setAgentSettings({
        max_chats_per_agent: full.max_chats_per_agent,
        ticket_assign_algorithm_id: full.ticket_assign_algorithm_id
      });

      setPromptDraft(full.generative_prompt || '');
    }
  }, [isSuccess, data]);

  const updatePartialSettings = (partial) => {
    const updated = { ...fullSettings, ...partial };
    updateSettings(updated);
    setFullSettings(updated);
  };

  const handleGeneralToggle = (key) => {
    const updated = { ...generalSettings, [key]: !generalSettings[key] };
    setGeneralSettings(updated);
    updatePartialSettings({ [key]: updated[key] });
  };

  const handlePromptSuggest = () => {
    const randomPrompt = defaultPrompts[Math.floor(Math.random() * defaultPrompts.length)];
    setPromptDraft(randomPrompt);
  };

  const handleSentimentToggle = () => {
    const toggled = !sentimentSettings.has_consecutive_negative_message_enabled;
    const updated = {
      has_consecutive_negative_message_enabled: toggled,
      consecutive_negative_message: sentimentSettings.consecutive_negative_message || 3
    };
    setSentimentSettings(prev => ({ ...prev, ...updated }));
    updatePartialSettings(updated);
  };

  const handleSaveSentiment = () => {
    const partial = {
      has_consecutive_negative_message_enabled: sentimentSettings.has_consecutive_negative_message_enabled,
      consecutive_negative_message: sentimentSettings.consecutive_negative_message
    };
    updatePartialSettings(partial);
  };

  const handleGenerativeToggle = () => {
    const toggled = !generativeSettings.has_auto_generate_responses_enabled;
    const updated = {
      has_auto_generate_responses_enabled: toggled,
      generative_prompt: generativeSettings.generative_prompt || defaultPrompts[0]
    };
    setGenerativeSettings(prev => ({ ...prev, ...updated }));
    setPromptDraft(updated.generative_prompt);
    updatePartialSettings(updated);
  };

  const handleSaveGenerative = () => {
    const partial = {
      has_auto_generate_responses_enabled: generativeSettings.has_auto_generate_responses_enabled,
      generative_prompt: promptDraft
    };
    updatePartialSettings(partial);
  };

  const handleSaveAgent = () => {
    const partial = {
      max_chats_per_agent: agentSettings.max_chats_per_agent,
      ticket_assign_algorithm_id: agentSettings.ticket_assign_algorithm_id
    };
    updatePartialSettings(partial);
  };

  return (
    <>
      {/* General Section */}
      <Flex><Heading3>General</Heading3></Flex>
      <InnerPanel>
        {isLoading && <Placeholder items={2} />}
        {!isLoading && generalSettings && (
          <Flex col ySpace>
            <Flex between middle center xSpace>
              <Flex col>
                <Heading3>Live chat</Heading3>
                <Span>When enabled, live chat is a fallback option.</Span>
              </Flex>
              <Toggle
                title="live-chat"
                on={generalSettings.enabled}
                handleToggleChange={() => handleGeneralToggle('enabled')}
              />
            </Flex>
            <Flex between middle center xSpace>
              <Flex col>
                <Heading3>Follow up</Heading3>
                <Span>Subscribers are asked to rate their live chat experience.</Span>
              </Flex>
              <Toggle
                title="follow-up"
                on={generalSettings.has_follow_ups_enabled}
                handleToggleChange={() => handleGeneralToggle('has_follow_ups_enabled')}
              />
            </Flex>
            <Flex between middle center xSpace>
              <Flex col>
                <Heading3>Agent feedback</Heading3>
                <Span>Agents can flag tickets for potential automation.</Span>
              </Flex>
              <Toggle
                title="agent-feedback"
                on={Boolean(generalSettings.has_agent_feedback_enabled)}
                handleToggleChange={() => handleGeneralToggle('has_agent_feedback_enabled')}
              />
            </Flex>
          </Flex>
        )}
      </InnerPanel>

      {/* Sentiment Analysis */}
      <GlobalFeatureGateFilter for={FEATURE_GATES.SENTIMENT_ANALYSIS}>
        <Flex><Heading3>Sentiment analysis</Heading3></Flex>
        <InnerPanel>
          {isLoading && <Placeholder items={2} />}
          {!isLoading && sentimentSettings && (
            <Flex col ySpace>
              <Flex between middle center xSpace>
                <Flex col>
                  <Heading3>Auto escalation</Heading3>
                  <Span>Escalate to live chat after consecutive negative messages</Span>
                </Flex>
                <Toggle
                  title="auto-escalation"
                  on={Boolean(sentimentSettings.has_consecutive_negative_message_enabled)}
                  handleToggleChange={handleSentimentToggle}
                />
              </Flex>
              {Boolean(sentimentSettings.has_consecutive_negative_message_enabled) && (
                <Flex col ySpace>
                  <Label htmlFor="consecutive_negative_message">Number Of Consecutive Negative Messages</Label>
                  <TextInput
                    name="consecutive_negative_message"
                    type="number"
                    placeholder="3"
                    value={sentimentSettings.consecutive_negative_message}
                    onChange={e =>
                      setSentimentSettings(prev => ({
                        ...prev,
                        consecutive_negative_message: e.target.value
                      }))
                    }
                  />
                  <Divider fullWidth />
                  <Flex right>
                    <Button variant="secondary" onClick={handleSaveSentiment} loading={isUpdating}>
                      Save
                    </Button>
                  </Flex>
                </Flex>
              )}
            </Flex>
          )}
        </InnerPanel>
      </GlobalFeatureGateFilter>

      {/* Generative AI */}
      <GlobalFeatureGateFilter for={FEATURE_GATES.GENERATIVE_AI}>
        <Flex><Heading3>Generative AI</Heading3></Flex>
        <InnerPanel>
          {isLoading && <Placeholder items={2} />}
          {!isLoading && generativeSettings && (
            <Flex col ySpace>
              <Flex between middle center xSpace>
                <Flex col>
                  <Heading3>Auto Generate Responses</Heading3>
                  <Span>Automatically query generative AI for answers using document sources.</Span>
                </Flex>
                <Toggle
                  title="auto-generate-responses"
                  on={Boolean(generativeSettings.has_auto_generate_responses_enabled)}
                  handleToggleChange={handleGenerativeToggle}
                  loading={isUpdating.toString()}
                />
              </Flex>
              {Boolean(generativeSettings.has_auto_generate_responses_enabled) && (
                <Flex col ySpace>
                  <Flex between middle center>
                    <Label htmlFor="generative_prompt">Prompt</Label>
                    <SmallButtonWrapper>
                      <Button variant="secondary" onClick={handlePromptSuggest}>Suggest Prompt</Button>
                    </SmallButtonWrapper>
                  </Flex>
                  <TextArea
                    rows={5}
                    id="generative_prompt"
                    placeholder="Type a prompt here..."
                    onChange={e => setPromptDraft(e.target.value)}
                    value={promptDraft}
                  />
                  <Divider fullWidth />
                  <Flex right>
                    <Button variant="secondary" onClick={handleSaveGenerative} loading={isUpdating}>
                      Save
                    </Button>
                  </Flex>
                </Flex>
              )}
            </Flex>
          )}
        </InnerPanel>
      </GlobalFeatureGateFilter>

      {/* Agents */}
      <Flex><Heading3>Agents</Heading3></Flex>
      <InnerPanel>
        {isLoading && <Placeholder items={2} />}
        {!isLoading && agentSettings && (
          <Flex col ySpace>
            <Label htmlFor="max_chats_per_agent">Maximum concurrent live chats</Label>
            <TextInput
              name="max_chats_per_agent"
              type="number"
              placeholder="3"
              value={agentSettings.max_chats_per_agent}
              onChange={e =>
                setAgentSettings(prev => ({
                  ...prev,
                  max_chats_per_agent: e.target.value
                }))
              }
            />
            <Label htmlFor="algorithm">Assignment algorithm</Label>
            <TicketAssignmentAlgorithmSelect
              id="algorithm"
              value={agentSettings.ticket_assign_algorithm_id}
              onChange={option =>
                setAgentSettings(prev => ({
                  ...prev,
                  ticket_assign_algorithm_id: option.value
                }))
              }
            />
            <Divider fullWidth />
            <Flex right>
              <Button variant="secondary" onClick={handleSaveAgent} loading={isUpdating}>
                Save
              </Button>
            </Flex>
          </Flex>
        )}
      </InnerPanel>

      {/* Active Hours */}
      <Flex col mt ySpace>
        <Flex mb between center>
          <Heading3>Active hours</Heading3>
          {timeZone && <Tag>{timeZone}</Tag>}
        </Flex>
        <InnerPanel>
          <ActiveHours query="/live-chat/active-hours" />
        </InnerPanel>
      </Flex>
    </>
  );
};

export default LiveChatSettings;